import {
  VITE_CALENDLY_TECH_URL,
  VITE_CALENDLY_VALUE_URL,
} from 'src/constants/envs';
import type { tCancellationReason } from '../../../SubscriptionManager.types';
import type { tRetentionSessionCta } from './CancelSubscription.types';

/**
 * A destination counts as configured only when it is an absolute http(s) URL.
 * An empty or blank build variable is how an environment switches a CTA off,
 * and anything else that does not parse would render a scheduler iframe
 * pointing at the app itself — so all of it reads as "not configured".
 */
export function readConfiguredUrl(
  value: string | null | undefined,
): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  try {
    const { protocol } = new URL(trimmed);
    return protocol === 'https:' || protocol === 'http:' ? trimmed : null;
  } catch {
    return null;
  }
}

/**
 * The booking page for the reason — the results consultation for "no veo
 * resultados", the technical session for everything else — or null when this
 * environment has none configured. A HubSpot meetings page or a Calendly event:
 * the scheduler prefills either (`buildRetentionBookingPageUrl`).
 */
export function retentionBookingUrl(
  reason: tCancellationReason | null,
): string | null {
  return readConfiguredUrl(
    reason === 'not_seeing_results'
      ? VITE_CALENDLY_VALUE_URL
      : VITE_CALENDLY_TECH_URL,
  );
}

/**
 * Where the retention session CTA goes: a 1:1 on the booking page for the
 * reason (`retentionBookingUrl`), whatever the plan and the time of day. A CTA
 * whose page is not configured is not offered at all, so the customer never
 * opens an empty scheduler.
 */
export function resolveRetentionSessionCta(
  bookingUrl: string | null,
): tRetentionSessionCta {
  return bookingUrl ? { kind: 'booking', url: bookingUrl } : { kind: 'none' };
}

/**
 * Whether a cancellation comment carries enough signal to be worth reading.
 *
 * 9.1% of the last 90 days of `retention_flow_sessions.details_text` is keyboard
 * mash, and the length histogram says why: 23 answers land in 25–30 characters and
 * 259 in 30–35. The distribution is shaped by the minimum, not by what people have
 * to say — so the gate is not collecting feedback, it is collecting compliance.
 *
 * The important consequence is the one that is easy to miss: the same gate corrupts
 * GOOD short answers. "Es muy caro el servicio" is 23 characters of perfectly usable
 * feedback, and production holds it as "Es muy caro el servicio.....................",
 * padded with dots to reach 30. A detector that reads raw text would flag that
 * customer — the one who actually answered — so padding is normalised away BEFORE
 * anything is judged, and only what survives is measured.
 *
 * This is deliberately a nudge and never a block. It runs on a screen someone is
 * already leaving, where a false positive costs far more than a mashed comment.
 */
export function assessCancellationDescription(text: string): {
  isLowInformation: boolean;
} {
  const normalised = text
    .toLowerCase()
    // "carooooo" and "....." are padding, not content: a run of the same character
    // says the same thing however long it is, so it counts once.
    .replace(/(.)\1{1,}/g, '$1$1')
    .replace(/[^\p{L}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // A token is word-like when it could be a word: a vowel and more than one letter.
  // "dd" and "xkcdfgh" are not; "caro" and "meses" are.
  const words = normalised
    .split(' ')
    .filter(
      (word) =>
        word.length > 1 &&
        /[aeiouáéíóúü]/.test(word) &&
        new Set(word).size > 1,
    );
  const distinctWords = new Set(words);

  const isLowInformation =
    normalised.length < 8 ||
    words.length < 2 ||
    // "asd asd asd asd" clears a word count but says one thing, once.
    (words.length >= 3 && distinctWords.size < 3) ||
    new Set(normalised.replace(/\s/g, '')).size <= 4;

  return { isLowInformation };
}
